import { useEffect, useState, useCallback, FC } from 'react'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { Combobox } from '@headlessui/react'
import classNames from 'classnames'
import { inputClasses } from 'ui/components/Form/Input'
import { useCurrentSite } from 'contexts/SiteConfig'
import useDebounce from 'hooks/Debounce'
import { CheckIcon } from '@heroicons/react/solid'

interface Props {
  requestedFields?: string[]
  onPlaceSelect: (place: google.maps.places.PlaceResult | null, placeId: string) => void
}

export type { Props as GoogleAddressAutoCompleteProps }

export const GoogleAddressAutoComplete: FC<Props> = ({
  onPlaceSelect,
  requestedFields = ['address_components'],
}) => {
  const places = useMapsLibrary('places')
  const site = useCurrentSite()
  const [inputValue, setInputValue] = useState<string>('')
  const debouncedInputValue = useDebounce(inputValue, 500)

  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken>()
  const [autocompleteService, setAutocompleteService] =
    useState<google.maps.places.AutocompleteService | null>(null)

  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | null>(null)

  const [predictionResults, setPredictionResults] = useState<
    Array<google.maps.places.AutocompletePrediction>
  >([])

  useEffect(() => {
    if (!places) return

    setAutocompleteService(new places.AutocompleteService())
    setPlacesService(
      new places.PlacesService(
        document.getElementById('googlemaps-attribution-container') as HTMLDivElement
      )
    )
    setSessionToken(new places.AutocompleteSessionToken())

    return () => setAutocompleteService(null)
  }, [places])

  const fetchPredictions = useCallback(
    async (inputValue: string) => {
      if (!autocompleteService || !inputValue) {
        setPredictionResults([])
        return
      }

      const request = { input: inputValue, region: `${site.country_code ?? 'GB'}`, sessionToken }
      const response = await autocompleteService.getPlacePredictions(request)

      setPredictionResults(response.predictions)
    },
    [autocompleteService, sessionToken, site.country_code]
  )

  useEffect(() => {
    if (debouncedInputValue) {
      fetchPredictions(debouncedInputValue)
    } else {
      setPredictionResults([])
    }
  }, [debouncedInputValue, fetchPredictions])

  const handleSuggestionClick = useCallback(
    (placeId: string) => {
      if (!places) return

      const detailRequestOptions: google.maps.places.PlaceDetailsRequest = {
        placeId,
        fields: requestedFields,
        sessionToken,
      }

      const detailsRequestCallback = (placeDetails: google.maps.places.PlaceResult | null) => {
        onPlaceSelect(placeDetails, placeId)

        setPredictionResults([])
        setInputValue(placeDetails?.formatted_address ?? '')
        setSessionToken(new places.AutocompleteSessionToken())
      }

      placesService?.getDetails(detailRequestOptions, detailsRequestCallback)
    },
    [onPlaceSelect, places, placesService, sessionToken, requestedFields]
  )

  return (
    <div>
      <div className="autocomplete-container">
        <Combobox as="div" value={inputValue}>
          {() => {
            return (
              <div className="relative mt-1">
                <Combobox.Input
                  className={inputClasses}
                  value={inputValue}
                  placeholder={'Type to search'}
                  onChange={(event) => setInputValue(event.target.value)}
                />
                {predictionResults.length > 0 && (
                  <Combobox.Options className="absolute z-20 mt-1 w-full bg-background-input shadow-lg max-h-60 rounded-md ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                    <ul className="p-1">
                      {predictionResults.map((result) => (
                        <Combobox.Option
                          key={result.place_id}
                          value={result}
                          onClick={() => handleSuggestionClick(result.place_id)}
                          className={({ active, disabled }) =>
                            classNames(
                              active ? 'bg-gray-100' : '',
                              disabled ? 'bg-gray-100 text-muted cursor-not-allowed' : '',
                              !active && !disabled ? ' cursor-default' : '',
                              'select-none relative p-2 pl-3 pr-9 rounded-md'
                            )
                          }
                        >
                          {({ selected, active }) => (
                            <>
                              <div>
                                <span className={classNames('block truncate')}>
                                  {result.structured_formatting.main_text}
                                </span>
                                <span className={classNames('block text-xs text-muted-foreground')}>
                                  {result.structured_formatting.secondary_text}
                                </span>
                              </div>

                              {selected ? (
                                <span
                                  className={classNames(
                                    'absolute inset-y-0 right-0 flex items-center pr-4'
                                  )}
                                >
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Combobox.Option>
                      ))}
                    </ul>
                  </Combobox.Options>
                )}
              </div>
            )
          }}
        </Combobox>
      </div>
      <div id="googlemaps-attribution-container"></div>
    </div>
  )
}
